import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap";
import { FormikProvider, useFormik } from "formik";
import BreadCrumb from "components/common/BreadCrumb";
import { BackButton } from "components/common/Buttons";
import PageLoader from "components/common/PageLoader";
import MaterialRequestBoqFieldArray, {
  materialRequestEmptyBoqRow,
} from "./MaterialRequestBoqFieldArray";
import { getDocumentTitle } from "utils/helpers";
import { toast } from "react-toastify";
import { MaterialRequestFormSource } from "../constants/material-request.constants";
import type {
  MaterialRequest,
  MaterialRequestSourceType,
} from "../types/material-request.types";
import {
  getWorkOrder,
  getWorkOrderOptions,
} from "features/WorkOrders/services/WorkOrderService";
import { workOrderAllowsMaterialRequest } from "features/WorkOrders/constants/work-order.constants";
import {
  WorkOrderOption,
  WorkOrderSingleValue,
} from "features/WorkOrders/components/WorkOrderSelectComponents";
import {
  getPettyWork,
  getPettyWorkOptions,
} from "features/PettyWorks/services/PettyWorkService";
import { pettyWorkAllowsMaterialRequest } from "features/PettyWorks/utils/pettyWorkActionPolicy";
import {
  PettyWorkOption,
  PettyWorkSingleValue,
} from "features/PettyWorks/components/PettyWorkSelectComponents";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import type { SelectOption } from "types/api.types";
import {
  createMaterialRequest,
  getMaterialRequest,
  submitMaterialRequest,
  updateMaterialRequest,
} from "../services/MaterialRequestService";
import {
  materialRequestDisplayCode,
  materialRequestEditBoqRowsToUpdateItems,
  materialRequestEligibleForEdit,
  materialRequestItemsToEditBoqRows,
  todayForDateInput,
  type MaterialRequestEditBoqRow,
} from "../utils/material-request.utils";
import { invalidatePendingMaterialRequestCount } from "../materialRequestPendingCountInvalidate";
import { pettyWorkItemsToMrBoqRows } from "../utils/pettyWorkItemsToMrBoqRows";
import { workOrderItemsToMrBoqRows } from "../utils/workOrderItemsToMrBoqRows";
import schema from "./schema";
import { QuotationItemType } from "features/Quotations/constants/quotation.constants";
import type { QuotationItemFormRow } from "features/Quotations/utils/quotationItem.utils";

type SourceType =
  | typeof MaterialRequestFormSource.WORK_ORDER
  | typeof MaterialRequestFormSource.PETTY_WORK;

const MaterialRequestFromJobPage = ({
  sourceType,
}: {
  sourceType: SourceType;
}) => {
  const { jobId: jobIdParam } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const jobId = Number(jobIdParam);
  const isWo = sourceType === MaterialRequestFormSource.WORK_ORDER;

  const [gateLoading, setGateLoading] = useState(true);
  const [gateOk, setGateOk] = useState(false);
  const [boqPresetItems, setBoqPresetItems] = useState<
    QuotationItemFormRow[] | null
  >(null);
  const [sourceDefaultOption, setSourceDefaultOption] = useState<
    SelectOption | undefined
  >();

  const jobLabel = isWo ? "work order" : "petty work";
  const listPath = isWo ? "/work-orders" : "/petty-works";

  document.title = getDocumentTitle(
    `Request materials (${isWo ? "work order" : "petty work"})`,
  );

  useEffect(() => {
    if (!Number.isFinite(jobId) || jobId <= 0) return;

    let cancelled = false;
    setGateLoading(true);
    setGateOk(false);
    setBoqPresetItems(null);
    void (async () => {
      try {
        if (isWo) {
          const res = await getWorkOrder(jobId);
          if (cancelled) return;
          if (!res.success || !res.data) {
            toast.error(res.message ?? "Could not load work order.");
            navigate("/inventory-management/material-requests", {
              replace: true,
            });
            return;
          }
          if (!workOrderAllowsMaterialRequest(res.data.status)) {
            toast.error(
              "Material requests are not allowed once this work order is completed, closed, or cancelled.",
            );
            navigate("/inventory-management/material-requests", {
              replace: true,
            });
            return;
          }
          const mapped = workOrderItemsToMrBoqRows(res.data.items ?? []);
          const materialRows = mapped.filter(
            (r) => r.type === QuotationItemType.MATERIAL,
          );
          if (!cancelled) {
            setBoqPresetItems(
              materialRows.length > 0
                ? materialRows
                : [materialRequestEmptyBoqRow()],
            );
            setSourceDefaultOption({
              value: res.data.id,
              label: res.data.code ?? `Work order #${res.data.id}`,
              title: res.data.title ?? null,
              status: res.data.status ?? null,
            } as SelectOption);
            setGateOk(true);
          }
        } else {
          const res = await getPettyWork(jobId);
          if (cancelled) return;
          if (!res.success || !res.data) {
            toast.error(res.message ?? "Could not load petty work.");
            navigate("/inventory-management/material-requests", {
              replace: true,
            });
            return;
          }
          if (!pettyWorkAllowsMaterialRequest(res.data.status)) {
            toast.error(
              "Material requests are not allowed once this petty work is finished, cancelled, or rejected.",
            );
            navigate("/inventory-management/material-requests", {
              replace: true,
            });
            return;
          }
          const mapped = pettyWorkItemsToMrBoqRows(res.data.items ?? []);
          const materialRows = mapped.filter(
            (r) => r.type === QuotationItemType.MATERIAL,
          );
          if (!cancelled) {
            setBoqPresetItems(
              materialRows.length > 0
                ? materialRows
                : [materialRequestEmptyBoqRow()],
            );
            setSourceDefaultOption({
              value: res.data.id,
              label: res.data.code ?? `Petty work #${res.data.id}`,
              title: res.data.title ?? null,
              status: res.data.status ?? null,
            } as SelectOption);
            setGateOk(true);
          }
        }
      } catch {
        if (!cancelled) {
          toast.error("Could not load job details.");
          navigate("/inventory-management/material-requests", {
            replace: true,
          });
        }
      } finally {
        if (!cancelled) setGateLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [jobId, isWo, navigate]);

  const initialFormValues = useMemo(() => {
    const rows =
      boqPresetItems != null && boqPresetItems.length > 0
        ? boqPresetItems.map((r) => ({ ...r }))
        : [materialRequestEmptyBoqRow()];
    return {
      requiredDate: todayForDateInput(),
      items: rows,
    };
  }, [boqPresetItems]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: initialFormValues,
    validationSchema: schema,
    onSubmit: async (values) => {
      const materialLines = (values.items as QuotationItemFormRow[]).filter(
        (row) => row.type === QuotationItemType.MATERIAL,
      );

      const isPetty = sourceType === MaterialRequestFormSource.PETTY_WORK;
      const isWo = sourceType === MaterialRequestFormSource.WORK_ORDER;
      const items = materialLines.map((row) => {
        const base = {
          quantityRequested: Number(row.quantity),
          unit: String(row.unit ?? "").trim() || undefined,
          rate: Number.isFinite(Number(row.rate)) ? Number(row.rate) : null,
          remarks: String(row.description ?? "").trim() || undefined,
          description: String(row.description ?? "").trim() || undefined,
        };
        if (
          isPetty &&
          row.id != null &&
          Number.isFinite(Number(row.id)) &&
          Number(row.id) > 0
        ) {
          return { ...base, pettyWorkItemId: Number(row.id) };
        }
        if (
          isWo &&
          row.id != null &&
          Number.isFinite(Number(row.id)) &&
          Number(row.id) > 0
        ) {
          return { ...base, workOrderItemId: Number(row.id) };
        }
        return base;
      });

      const requiredDate = String(values.requiredDate ?? "").trim();
      if (!requiredDate) {
        toast.error("Required date is required.");
        return;
      }

      try {
        const res = await createMaterialRequest({
          sourceType: sourceType as MaterialRequestSourceType,
          sourceId: jobId,
          requiredDate,
          items,
        });

        if (!res.success) {
          toast.error(res.message ?? "Could not create material request.");
          return;
        }

        const newId = res.data?.id;
        if (newId) {
          try {
            const submitted = await submitMaterialRequest(newId);
            if (!submitted.success) {
              toast.info(submitted.message ?? "Saved as draft");
            }
          } catch {
            toast.info("Material request created");
          }
          invalidatePendingMaterialRequestCount();
          navigate(-1);
        } else {
          toast.success(res.message ?? "Material request created.");
          invalidatePendingMaterialRequestCount();
          navigate(-1);
        }
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ??
          (err as Error)?.message ??
          "Request failed.";
        toast.error(msg);
      }
    },
  });

  if (!Number.isFinite(jobId) || jobId <= 0) {
    return <Navigate to="/inventory-management/material-requests" replace />;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Request materials"
          pageTitle="Inventory Management"
          items={[
            {
              label: "Material requests",
              path: "/inventory-management/material-requests",
            },
            {
              label: isWo ? "Work orders" : "Petty works",
              path: listPath,
            },
          ]}
        />

        <div className="mb-3">
          <Label className="form-label fw-semibold">
            {isWo ? "Work order" : "Petty work"}
          </Label>
          <AsyncPaginatedSelect
            fetchOptions={isWo ? getWorkOrderOptions : getPettyWorkOptions}
            components={
              isWo
                ? { Option: WorkOrderOption, SingleValue: WorkOrderSingleValue }
                : { Option: PettyWorkOption, SingleValue: PettyWorkSingleValue }
            }
            placeholder={`Select ${jobLabel}…`}
            value={jobId}
            defaultOption={sourceDefaultOption}
            onChange={() => {}}
            isDisabled
          />
        </div>

        {gateLoading || !gateOk ? (
          <PageLoader message={`Loading ${jobLabel}…`} />
        ) : (
          <FormikProvider value={validation}>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                void validation.submitForm();
              }}
            >
              <Row className="justify-content-center">
                <Col>
                  <Card className="shadow-none border">
                    <CardHeader className="border-bottom bg-transparent pb-3">
                      <h5 className="card-title mb-0">Request materials</h5>
                      <p className="text-muted mb-0 small">
                        Fill in the required date and material lines for this{" "}
                        {jobLabel}.
                      </p>
                    </CardHeader>
                    <CardBody className="pt-4">
                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                        Required date
                      </div>
                      <Row className="g-3 mb-4">
                        <Col md={6} lg={4}>
                          <Label className="form-label" for="mr-required-date">
                            Date <span className="text-danger">*</span>
                          </Label>
                          <Input
                            id="mr-required-date"
                            type="date"
                            name="requiredDate"
                            value={validation.values.requiredDate}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              !!(
                                validation.touched.requiredDate &&
                                validation.errors.requiredDate
                              )
                            }
                          />
                          {validation.touched.requiredDate &&
                          validation.errors.requiredDate ? (
                            <FormFeedback type="invalid" className="d-block">
                              {String(validation.errors.requiredDate)}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>

                      <hr className="my-4 text-muted opacity-25" />

                      <MaterialRequestBoqFieldArray
                        validation={validation}
                        jobKind={isWo ? "work_order" : "petty_work"}
                      />
                    </CardBody>
                    <CardFooter className="bg-light bg-opacity-50 border-top d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
                      <BackButton />
                      <Button
                        type="submit"
                        color="primary"
                        className="btn-label px-4"
                        disabled={validation.isSubmitting}
                      >
                        {validation.isSubmitting ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            />
                            Submitting…
                          </>
                        ) : (
                          "Create request"
                        )}
                      </Button>
                    </CardFooter>
                  </Card>
                </Col>
              </Row>
            </Form>
          </FormikProvider>
        )}
      </Container>
    </div>
  );
};

function MaterialRequestEditPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const id = Number(idParam);
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState<MaterialRequest | null>(null);

  document.title = getDocumentTitle("Edit material request");

  useEffect(() => {
    if (!Number.isFinite(id) || id <= 0) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const res = await getMaterialRequest(id);
        if (cancelled) return;
        if (!res.success || !res.data) {
          toast.error(res.message ?? "Could not load material request.");
          setRecord(null);
          return;
        }
        if (!materialRequestEligibleForEdit(res.data)) {
          navigate("/inventory-management/material-requests", {
            replace: true,
          });
          return;
        }
        setRecord(res.data);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            (err as { response?: { data?: { message?: string } } })?.response
              ?.data?.message ?? "Could not load material request.",
          );
          setRecord(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const initialValues = useMemo(() => {
    if (!record)
      return { requiredDate: "", items: [materialRequestEmptyBoqRow()] };
    const rows = materialRequestItemsToEditBoqRows(record.items);
    return {
      requiredDate: record.requiredDate
        ? String(record.requiredDate).slice(0, 10)
        : "",
      items:
        rows.length > 0
          ? rows.map((r) => ({ ...r }))
          : [materialRequestEmptyBoqRow()],
    };
  }, [record]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: async (values) => {
      if (!record) return;
      const materialLines = (
        values.items as MaterialRequestEditBoqRow[]
      ).filter((row) => row.type === QuotationItemType.MATERIAL);
      const items = materialRequestEditBoqRowsToUpdateItems(
        materialLines,
        record.sourceType,
      );
      if (!items.length) {
        toast.error("Add at least one material line.");
        return;
      }
      const requiredDate = String(values.requiredDate ?? "").trim();
      if (!requiredDate) {
        toast.error("Required date is required.");
        return;
      }
      try {
        const res = await updateMaterialRequest(record.id, {
          requiredDate,
          items,
        });
        if (!res.success) {
          toast.error(res.message ?? "Could not update material request.");
          return;
        }
        toast.success(res.message ?? "Material request updated.");
        invalidatePendingMaterialRequestCount();
        navigate(`/inventory-management/material-requests/${record.id}`);
      } catch (err) {
        toast.error(
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? "Request failed.",
        );
      }
    },
  });

  const jobKind =
    record?.sourceType === MaterialRequestFormSource.PETTY_WORK
      ? "petty_work"
      : "work_order";

  if (!Number.isFinite(id) || id <= 0) {
    return <Navigate to="/inventory-management/material-requests" replace />;
  }

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <PageLoader message="Loading material request…" />
        </Container>
      </div>
    );
  }

  if (!record) {
    return <Navigate to="/inventory-management/material-requests" replace />;
  }

  const displayCode = materialRequestDisplayCode(record);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Edit material request"
          pageTitle="Inventory Management"
          items={[
            {
              label: "Material requests",
              path: "/inventory-management/material-requests",
            },
          ]}
        />

        <Alert color="light" className="border mb-3">
          <span className="me-2">Editing {displayCode}.</span>
          <Link to={`/inventory-management/material-requests/${record.id}`}>
            Open detail
          </Link>
        </Alert>

        <FormikProvider value={validation}>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              void validation.submitForm();
            }}
          >
            <Row className="justify-content-center">
              <Col>
                <Card className="shadow-none border">
                  <CardHeader className="border-bottom bg-transparent pb-3">
                    <h5 className="card-title mb-0">Edit material request</h5>
                    <p className="text-muted mb-0 small">
                      Update the required date and material lines for this
                      request.
                    </p>
                  </CardHeader>
                  <CardBody className="pt-4">
                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                      Required date
                    </div>
                    <Row className="g-3 mb-4">
                      <Col md={6} lg={4}>
                        <Label
                          className="form-label"
                          for="mr-edit-required-date"
                        >
                          Date <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="mr-edit-required-date"
                          type="date"
                          name="requiredDate"
                          value={validation.values.requiredDate}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            !!(
                              validation.touched.requiredDate &&
                              validation.errors.requiredDate
                            )
                          }
                        />
                        {validation.touched.requiredDate &&
                        validation.errors.requiredDate ? (
                          <FormFeedback type="invalid" className="d-block">
                            {String(validation.errors.requiredDate)}
                          </FormFeedback>
                        ) : null}
                      </Col>
                    </Row>

                    <hr className="my-4 text-muted opacity-25" />

                    <MaterialRequestBoqFieldArray
                      validation={validation}
                      jobKind={jobKind}
                    />
                  </CardBody>
                  <CardFooter className="bg-light bg-opacity-50 border-top d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
                    <BackButton />
                    <Button
                      type="submit"
                      color="primary"
                      className="btn-label px-4"
                      disabled={validation.isSubmitting}
                    >
                      {validation.isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                          Saving…
                        </>
                      ) : (
                        "Save changes"
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              </Col>
            </Row>
          </Form>
        </FormikProvider>
      </Container>
    </div>
  );
}

export type MaterialRequestFormProps =
  | { mode: "edit" }
  | { mode?: "from-job"; sourceType: SourceType };

export default function MaterialRequestForm(props: MaterialRequestFormProps) {
  if ("mode" in props && props.mode === "edit") {
    return <MaterialRequestEditPage />;
  }
  return (
    <MaterialRequestFromJobPage
      sourceType={(props as { sourceType: SourceType }).sourceType}
    />
  );
}
